import callApi from '@niarab2c/frontend-commons/src/util/callApi';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import queryString from 'query-string';
import { LoyaltyConfiguration } from 'niara-spear-crudmodel';
import type { CompleteRootState } from '../base';
export type Balance = Pick<LoyaltyConfiguration, 'allowDecimals' | 'showTierOnNavbar' | 'currency' | 'loyaltyType' | 'unitText'> & {
  balance: number;
  programAlias: string;
  programType: string;
  tier: string;
  tierUpdateDateTime: string;
  tierExpirationDateTime: string;
  contractStatus: 'ACTIVE' | 'INACTIVE' | 'BLOCKED';
  TSER_idContrato: number;
  TSER_numeroContrato: string;
  loyaltyOptIn: boolean;
  loyaltyConfigurationId?: string;
  nextTier?: {
    name?: string;
    percentage?: number;
    valueToUpdate?: number;
    valueToUpdateInt?: number;
  };
};
export type BalanceV2 = {
  /**
   * @deprecated
   */
  balance: Balance;
  /**
   * Lista dos balances do usuário, incluindo múltiplas carteiras ou
   * integrações externas (ex: contratos do TSER)
   */
  balances: Balance[];
  /**
   * Diferente de balances, esta lista é para casos em que o usuário precisa
   * escolher entre diferentes contratos TSER na hora de pesquisar/reservar.
   * No futuro, se trabalharmos com múltiplas carteiras, este item pode
   * ser usado para isso.
   */
  loyaltyOptions: Balance[];
};
export interface LoyaltyState {
  userBalance?: number;
  unitText?: string;
  userBalanceProgramType?: string;
  userBalanceContractStatus?: 'ACTIVE' | 'INACTIVE';
  tier?: string;
  showTierOnNavbar?: boolean;
  loyaltyType?: 'CASHBACK' | 'POINTS';
  loyaltyCurrency?: string;
  loyaltyOptions?: Balance[];
  loyaltyOptIn?: boolean;
  TSER_idContrato?: number;
  TSER_numeroContrato?: string;
}
export const refreshBalance = createAsyncThunk<Partial<LoyaltyState> | null, void | {
  personId: string;
}, {
  state: CompleteRootState;
}>('loyalty/refreshBalance', async (params, {
  getState
}) => {
  const rootState = getState();
  const b2c = rootState?.authentication?.b2c;

  // Só chama o balance se for b2c
  if (!b2c) return null;
  const user = rootState?.authentication?.user;
  const personId = params ? params.personId : user?.personId;
  // Não chama o balance se não for possível identificar o usuário
  if (!personId) {
    return null;
  }
  const clientId = rootState.core.clientId;
  const tenantId = rootState.core.tenantId;
  const landingPageId = rootState.storefrontConfig?.storefront?.landingPageId;
  const shoppingCartState = rootState.shoppingCart;
  // propriedade do carrinho pode escolher motor diferente - portanto, enviar
  const propertyIds = shoppingCartState?.items?.map(cartItem => cartItem?.hotelResult.hotel.propertyId).filter(x => x);
  // O que fazer se tem mais de uma?
  const propertyId = propertyIds?.[0];
  const loyaltyRedeemId = shoppingCartState?.loyaltyRedeemId;
  const result = (await callApi('niara-spear-loyalty-programs', `/loyalty-programs/balance_v2?${queryString.stringify({
    clientId,
    tenantId,
    landingPageId,
    propertyId
  })}`).catch(() => null)) as BalanceV2;
  const loyaltyBalance = result?.balances?.find(b => b.loyaltyConfigurationId == loyaltyRedeemId) ?? result?.balances?.find(b => b?.loyaltyType == 'CASHBACK') ?? result?.balances?.find(b => b?.loyaltyType == 'POINTS');
  const loyaltyTier = result?.balances?.find(b => b?.tier);
  return {
    userBalance: loyaltyBalance?.balance ?? null,
    unitText: loyaltyBalance?.unitText ?? "pontos",
    userBalanceProgramType: result?.[0]?.balance ?? loyaltyBalance?.programType ?? null,
    userBalanceContractStatus: loyaltyBalance?.contractStatus as 'ACTIVE' | 'INACTIVE',
    tier: loyaltyTier?.tier,
    showTierOnNavbar: loyaltyTier?.showTierOnNavbar,
    loyaltyType: loyaltyBalance?.loyaltyType,
    loyaltyCurrency: loyaltyBalance?.currency,
    loyaltyOptions: result?.loyaltyOptions,
    loyaltyOptIn: result?.balances?.some(b => b?.loyaltyOptIn),
    TSER_idContrato: loyaltyBalance?.TSER_idContrato,
    TSER_numeroContrato: loyaltyBalance?.TSER_numeroContrato
  }; //v1: result?.[0]?.balance  v2: result?.balance
});
const loyaltySlice = createSlice({
  name: 'loyalty',
  initialState: {} as LoyaltyState,
  reducers: {
    setLoyaltyOption: (state, action: PayloadAction<{
      TSER_idContrato?: number;
    }>) => {
      const loyaltyBalance = state.loyaltyOptions?.find(l => l.TSER_idContrato == action.payload.TSER_idContrato);
      if (loyaltyBalance) {
        return {
          ...state,
          userBalance: loyaltyBalance?.balance ?? null,
          unitText: loyaltyBalance?.unitText ?? "pontos",
          userBalanceProgramType: loyaltyBalance?.programType ?? null,
          userBalanceContractStatus: loyaltyBalance?.contractStatus as 'ACTIVE' | 'INACTIVE',
          tier: loyaltyBalance?.tier,
          showTierOnNavbar: loyaltyBalance?.showTierOnNavbar,
          loyaltyType: loyaltyBalance?.loyaltyType as any,
          loyaltyCurrency: loyaltyBalance?.currency,
          TSER_idContrato: loyaltyBalance?.TSER_idContrato,
          TSER_numeroContrato: loyaltyBalance?.TSER_numeroContrato
        };
      }
    }
  },
  extraReducers: builder => {
    builder.addCase(refreshBalance.fulfilled, (state, action) => {
      if (action.payload) {
        return {
          ...state,
          ...action.payload
        };
      } else {
        return state;
      }
    });
  }
});
export const {
  setLoyaltyOption
} = loyaltySlice.actions;
export default loyaltySlice.reducer;